import React, { useEffect, useState } from 'react';

import { VALIDATION_REGEX } from '../../../../../shared/components/validation.config';
import { FormElementData } from '../../../../../shared/interfaces/form-element-data.interface';
import { trackEvent } from '../../../../../shared/services/utils/tracking/tracking.service';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import {
  InputText,
  InputTextErrorMode,
  InputTextType
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import { BrokerUiModel } from '../../../../../shared/ui-models/broker.ui-model';

export interface EditBrokerModalProps {
  data: {
    broker?: BrokerUiModel;
  };
  config: {
    showModal: boolean;
  };
  eventHandlers: {
    cancelHandler: () => void;
    editBrokerHandler: (brokerId: number, broker: Partial<BrokerUiModel>) => void;
  };
}

export const EditBrokerModal: React.FC<EditBrokerModalProps> = ({
  data,
  config,
  eventHandlers
}) => {
  const [brokerName, setBrokerName] = useState<FormElementData<string>>({
    value: '',
    isValid: false,
    changed: false
  });
  const [contactPersonName, setContactPersonName] = useState<FormElementData<string>>({
    value: '',
    isValid: true,
    changed: false
  });
  const [contactPersonEmail, setContactPersonEmail] = useState<FormElementData<string>>({
    value: '',
    isValid: true,
    changed: false
  });
  const [contactPersonPhone, setContactPersonPhone] = useState<FormElementData<string>>({
    value: '',
    isValid: true,
    changed: false
  });
  const isMobileView = useWindowResizer();

  useEffect(() => {
    setBrokerName({ value: data?.broker?.name || '', isValid: false, changed: false });
    setContactPersonName({
      value: data?.broker?.contactPersonName || '',
      isValid: true,
      changed: false
    });
    setContactPersonEmail({
      value: data?.broker?.contactPersonEmail || '',
      isValid: true,
      changed: false
    });
    setContactPersonPhone({
      value: data?.broker?.contactPersonPhoneNumber || '',
      isValid: true,
      changed: false
    });
  }, [data.broker]);

  const handleBrokerSubmit = () => {
    if (typeof data.broker?.id !== 'number') return;

    const updatePayload: Partial<BrokerUiModel> = {};
    let isValid = true;

    if (brokerName.changed) {
      isValid = brokerName.isValid && isValid;
      if (brokerName.isValid && brokerName.value !== data.broker?.name) {
        updatePayload.name = brokerName.value;
      }
    }

    if (contactPersonName.changed) {
      isValid = contactPersonName.isValid && isValid;
      if (contactPersonName.isValid && contactPersonName.value !== data.broker?.contactPersonName) {
        updatePayload.contactPersonName = contactPersonName.value;
      }
    }

    if (contactPersonEmail.changed) {
      isValid = contactPersonEmail.isValid && isValid;
      if (
        contactPersonEmail.isValid &&
        contactPersonEmail.value !== data.broker?.contactPersonEmail
      ) {
        updatePayload.contactPersonEmail = contactPersonEmail.value;
      }
    }

    if (contactPersonPhone.changed) {
      isValid = contactPersonPhone.isValid && isValid;
      if (
        contactPersonPhone.isValid &&
        contactPersonPhone.value !== data.broker?.contactPersonPhoneNumber
      ) {
        updatePayload.contactPersonPhoneNumber = contactPersonPhone.value;
      }
    }

    if (isValid && Object.keys(updatePayload).length > 0) {
      eventHandlers.editBrokerHandler(data.broker.id, updatePayload);
    }
  };

  return (
    <Modal
      config={{ showModal: config.showModal, fullScreen: false, showClose: true }}
      eventHandlers={{
        closeModalHandler: () => {
          eventHandlers.cancelHandler();
        }
      }}
    >
      <div className="flex flex-col gap-3 px-6 py-4">
        <div className="text-2xl font-gilroy-bold font-semibold text-neutral-900">Edit Broker</div>
        {/*
          Renaming a broker does not rewrite the reservations already credited
          to them -- `brokerName` is stored per reservation so a payout report
          still reads the way it did when the booking was made.
        */}
        <div className="text-xl font-medium font-gilroy-medium text-neutral-500">Broker name</div>
        <InputText
          data={{
            value: brokerName.value,
            errorMessage: 'Please enter a valid broker name',
            placeholder: 'Enter broker name'
          }}
          config={{
            maxRows: 1,
            autoFocus: true,
            validationRegex: VALIDATION_REGEX.COMPANY_NAME,
            showErrorMode: InputTextErrorMode.instant,
            required: true
          }}
          eventHandlers={{
            onChangeHandler: (value: string, isValid: boolean) => {
              setBrokerName({ value, isValid, changed: true });
            },
            onEnterPressedHandler: handleBrokerSubmit
          }}
        />
        <div className="text-xl font-medium font-gilroy-medium text-neutral-500">
          Contact person name (optional)
        </div>
        <InputText
          data={{
            value: contactPersonName.value,
            errorMessage: 'Please enter a valid name',
            placeholder: 'Enter contact person name'
          }}
          config={{
            maxRows: 1,
            showErrorMode: InputTextErrorMode.instant
          }}
          eventHandlers={{
            onChangeHandler: (value: string, isValid: boolean) => {
              setContactPersonName({ value, isValid: isValid || !value, changed: true });
            },
            onEnterPressedHandler: handleBrokerSubmit
          }}
        />
        <div className="text-xl font-medium font-gilroy-medium text-neutral-500">
          Contact person email (optional)
        </div>
        <InputText
          data={{
            value: contactPersonEmail.value,
            errorMessage: 'Please enter a valid email',
            placeholder: 'Enter contact person email'
          }}
          config={{
            maxRows: 1,
            type: InputTextType.email,
            validationRegex: VALIDATION_REGEX.EMAIL,
            showErrorMode: InputTextErrorMode.instant
          }}
          eventHandlers={{
            onChangeHandler: (value: string, isValid: boolean) => {
              setContactPersonEmail({ value, isValid: isValid || !value, changed: true });
            },
            onEnterPressedHandler: handleBrokerSubmit
          }}
        />
        <div className="text-xl font-medium font-gilroy-medium text-neutral-500">
          Contact person phone (optional)
        </div>
        <InputText
          data={{
            value: contactPersonPhone.value,
            errorMessage: 'Please enter a phone number',
            placeholder: 'Enter contact person phone'
          }}
          config={{
            maxRows: 1,
            showErrorMode: InputTextErrorMode.instant
          }}
          eventHandlers={{
            onChangeHandler: (value: string, isValid: boolean) => {
              setContactPersonPhone({ value, isValid: isValid || !value, changed: true });
            },
            onEnterPressedHandler: handleBrokerSubmit
          }}
        />
      </div>
      <div className="flex flex-col gap-3 px-6 pb-6 sm:flex-row sm:justify-end">
        <Button
          onClick={() => {
            eventHandlers.cancelHandler();
            trackEvent('record_edit_cancelled', { entity: 'broker', method: 'cancel' });
          }}
          tracking={{ event: 'record_edit_cancelled', entity: 'broker', method: 'cancel' }}
          size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          type={ButtonType.primaryInverted}
        >
          Cancel
        </Button>
        <Button
          onClick={handleBrokerSubmit}
          tracking={{
            event: 'record_saved',
            entity: 'broker',
            action: 'update',
            outcome: 'success'
          }}
          size={isMobileView ? ButtonSize.full : ButtonSize.medium}
        >
          Save
        </Button>
      </div>
    </Modal>
  );
};
